import React, { useState, useRef } from "react";
import { Button } from "@/utils/ui/Button/Button";
import AiIcon from "../../assets/icons/svg/AI-icon.svg?react";
import {
  useClickOutside,
  addToHistory,
  handleHistoryNavigation,
} from "../../utils";
import { store } from "../../store";
import { aiService } from "@/store/app/app.actions";
import BackIcon from "../../assets/icons/svg/chevron_left.svg?react";
import ForwardIcon from "../../assets/icons/svg/chevron_right.svg?react";
import { trackEvent, getTrackUserContext } from "@/utils/analytics";

interface AIAssistButtonsProps {
  field: "title" | "description";
  textToProcess: string;
  mediaId: string;
  targetLanguage?: string;
  onResponseReceived: (response: string) => void;
  className?: string;
  style?: React.CSSProperties;
  position?: "left" | "right";
  isWizard?: boolean;
  onCustomPromptClick?: () => void;
  customPromptOpen?: boolean;
  onCustomPromptClose?: () => void;
  isOpen: boolean;
  onToggle: () => void;
  setisDescriptionAiButtonLoading?: (isLoading: boolean) => void;
  historyFromProps?: string[];
  currentHistoryIndex?: number;
  onHistoryNavigation?: (direction: "back" | "forward") => void;
  onAddToHistory?: (text: string) => void;
  // Props for common history methods
  setHistory?: React.Dispatch<React.SetStateAction<string[]>>;
  setIndex?: React.Dispatch<React.SetStateAction<number>>;
  formik?: any;
}

export const AIAssistButtons: React.FC<AIAssistButtonsProps> = ({
  field,
  textToProcess,
  mediaId,
  onResponseReceived,
  className = "",
  style = {},
  position = "right",
  isWizard = false,
  onCustomPromptClick,
  customPromptOpen = false,
  onCustomPromptClose,
  isOpen,
  onToggle,
  historyFromProps,
  setisDescriptionAiButtonLoading,
  currentHistoryIndex: externalCurrentHistoryIndex,
  onHistoryNavigation,
  onAddToHistory,
  setHistory,
  setIndex,
  formik,
}) => {
  const [showTranslateSubmenu, setShowTranslateSubmenu] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [responseHistory, setResponseHistory] = useState<string[]>(
    historyFromProps || []
  );
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1);
  const ref = useRef<HTMLDivElement>(null);

  // Use external history if provided, otherwise use internal state
  const effectiveHistory = historyFromProps || responseHistory;
  const effectiveCurrentIndex =
    externalCurrentHistoryIndex !== undefined
      ? externalCurrentHistoryIndex
      : currentHistoryIndex;

  useClickOutside(ref, () => {
    if (isOpen) onToggle();
    setShowTranslateSubmenu(false);
  });

  const handleDropdownToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!textToProcess.trim()) return;

    if (customPromptOpen && onCustomPromptClose) {
      onCustomPromptClose();
    } else {
      onToggle();
    }
  };

  const handleTranslateClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowTranslateSubmenu(!showTranslateSubmenu);
  };

  const handleLanguageSelect = (language: string) => {
    handleAIOptionClick("translate", language);
    setShowTranslateSubmenu(false);
    onToggle();
  };

  const handleAIOptionClick = (option: string, targetLanguage?: string) => {
    // Analytics: AI Transform apply
    trackEvent("upload", {
      category: "AI_Transform",
      entity: option,
      action: "apply",
      location: isWizard ? "wizard" : "non-wizard",
      ...getTrackUserContext(),
    });

    if (option === "translate" && !targetLanguage) return;
    if (
      textToProcess &&
      (effectiveHistory.length === 0 ||
        effectiveHistory[effectiveHistory.length - 1] !== textToProcess)
    ) {
      if (onAddToHistory) {
        onAddToHistory(textToProcess);
      } else if (setHistory && setIndex) {
        addToHistory(textToProcess, setHistory, setIndex);
      } else {
        addToHistory(textToProcess, setResponseHistory, setCurrentHistoryIndex);
      }
    }

    setIsProcessing(true);
    setisDescriptionAiButtonLoading?.(true);
    onToggle();
    store
      .dispatch(
        aiService({
          title: textToProcess,
          id: mediaId,
          service: option,
          target_language: targetLanguage || "",
          type: field,
        })
      )
      .then((response: any) => {
        if (response) {
          const resultText = response.output_processed.output;

          if (resultText && onResponseReceived) {
            if (onAddToHistory) {
              onAddToHistory(resultText);
            } else if (setHistory && setIndex) {
              addToHistory(resultText, setHistory, setIndex);
            } else {
              addToHistory(
                resultText,
                setResponseHistory,
                setCurrentHistoryIndex
              );
            }
            onResponseReceived(resultText);
          }
        }
      })
      .catch((error: any) => {
        console.error("Error in AI service:", error);
      })
      .finally(() => {
        setIsProcessing(false);
        setShowTranslateSubmenu(false);
      });
  };

  const handleHistoryNavigationClick = (direction: "back" | "forward") => {
    if (onHistoryNavigation) {
      onHistoryNavigation(direction);
    } else if (setHistory && setIndex && formik) {
      handleHistoryNavigation(
        direction,
        effectiveHistory,
        effectiveCurrentIndex,
        setIndex,
        field,
        formik
      );
    } else {
      if (direction === "back" && effectiveCurrentIndex > 0) {
        const newIndex = effectiveCurrentIndex - 1;
        setCurrentHistoryIndex(newIndex);
        onResponseReceived(effectiveHistory[newIndex]);
      } else if (
        direction === "forward" &&
        effectiveCurrentIndex < effectiveHistory.length - 1
      ) {
        const newIndex = effectiveCurrentIndex + 1;
        setCurrentHistoryIndex(newIndex);
        onResponseReceived(effectiveHistory[newIndex]);
      }
    }
  };

  const HistoryNavigation = () => {
    const hasHistory = effectiveHistory.length > 0;
    const canGoBack = effectiveCurrentIndex > 0;
    const canGoForward = effectiveCurrentIndex < effectiveHistory.length - 1;
    const isLoading = isProcessing;

    if (!hasHistory) return null;
    return (
      <div className="d-flex align-items-center gap-2">
        <BackIcon
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleHistoryNavigationClick("back");
          }}
          style={{
            cursor: !canGoBack || isLoading ? "not-allowed" : "pointer",
            opacity: !canGoBack || isLoading ? "0.5" : "1",
            fontSize: "16px",
            textDecoration: "none",
          }}
          aria-disabled={!canGoBack || isLoading}
        />

        <span className="fs-6 text-muted" style={{ width: "30px" }}>{`${
          effectiveCurrentIndex + 1
        } / ${effectiveHistory.length}`}</span>

        <ForwardIcon
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleHistoryNavigationClick("forward");
          }}
          style={{
            cursor: !canGoForward || isLoading ? "not-allowed" : "pointer",
            opacity: !canGoForward || isLoading ? "0.5" : "1",
            fontSize: "16px",
            textDecoration: "none",
          }}
          aria-disabled={!canGoForward || isLoading}
        />
      </div>
    );
  };

  return (
    <div className={`position-relative d-flex ${className}`} style={style}>
      <>
        <HistoryNavigation />
        <Button
          type="outline-primary"
          className="aiButton py-1"
          // style={{width: '30px'}}
          onClick={handleDropdownToggle}
          isDisabled={isProcessing || !textToProcess.trim()}
          HTMLButtonType="button"
        >
          {isProcessing ? (
            <div className="spinner-border spinner-border-sm" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          ) : customPromptOpen ? (
            <i className="bi bi-x fs-4"></i>
          ) : (
            <AiIcon style={{ width: "18px" }} />
          )}
        </Button>
      </>
      {isOpen && !customPromptOpen && textToProcess.trim() && (
        <div
          ref={ref}
          className="position-absolute top-30 mt-1 bg-white border rounded shadow-sm"
          style={{ zIndex: 10, [position]: 0 }}
        >
          <ul className="drop-menu list-unstyled mb-0">
            {[
              { id: "Short & Concise", label: "Short & Concise" },
              { id: "Factual", label: "Factual" },
              { id: "Conversational", label: "Conversational" },
              { id: "Dramatic", label: "Dramatic" },
              { id: "Viral", label: "Viral" },
            ].map((item) => (
              <li
                key={item.id}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleAIOptionClick(item.id);
                }}
              >
                {item.label}
              </li>
            ))}
            <li
              className="d-flex justify-content-between position-relative"
              onClick={handleTranslateClick}
            >
              Translate
              <i className="bi bi-chevron-right ms-2"></i>
              {showTranslateSubmenu && (
                <div
                  className="position-absolute top-0 start-100 bg-white border rounded shadow-sm"
                  style={{ minWidth: "150px" }}
                >
                  <ul className="list-unstyled mb-0">
                    {["English", "Hindi", "Bengali", "Tamil", "Telugu"].map(
                      (lang) => (
                        <li
                          key={lang}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleLanguageSelect(lang);
                          }}
                        >
                          {lang}
                        </li>
                      )
                    )}
                  </ul>
                </div>
              )}
            </li>
            <li
              onClick={() => {
                if (onCustomPromptClick) onCustomPromptClick();
                onToggle();
              }}
            >
              Custom Prompt
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};
